import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PhoneForwarded } from 'lucide-react';
import { Button, toast } from '@cyphex/ui';
import { ROUTES } from '@/constants/routes';
import { callsApi } from '../../data/callsApi';
import { useCallSurfaceExit } from '../../hooks/useCallSurfaceExit';
import { useOpenCall } from '../../hooks/useOpenCall';
import {
  pendingTransferActions,
  type PendingTransfer,
} from '../../store/pendingTransferStore';

export interface ConsultTransferBarProps {
  transfer: PendingTransfer;
  /** Merging is the third answer: keep everybody on one call instead. */
  onMerge: () => void;
  busy: boolean;
}

/**
 * THE BAR THAT FINISHES AN ATTENDED TRANSFER.
 *
 * `POST /transfer/:id/complete` and `/cancel` both worked and neither had any
 * caller: once a consultative transfer was started there was no way in the
 * product to finish it or to back out, so the transfer row sat `pending` and
 * the original call stayed parked until a sweeper reaped it.
 *
 * All three answers to "can you take this call?" are here, and only here:
 * complete (they take it), merge (everybody talks), cancel (go back).
 */
export function ConsultTransferBar(props: ConsultTransferBarProps) {
  const exit = useCallSurfaceExit();
  const openCall = useOpenCall();
  const { transfer } = props;

  const finish = useMutation({
    mutationFn: () => callsApi.completeTransfer(transfer.originalCallId, transfer.transferId),
    onSuccess: () => {
      pendingTransferActions.clear(transfer.transferId);
      toast.success(`Call handed to ${transfer.targetName}.`);
      /* Completing DROPS YOU OUT of the original call — you handed it over —
         and ends the consult leg you are standing in, so neither call is a
         place to stay. In a desktop call window there is nowhere to go at all:
         `/calls` would turn the window into a second workspace, so it closes
         and leaves the workspace on whatever the user was already reading. */
      exit.leave(ROUTES.calls);
    },
    onError: () => toast.error('Could not complete that transfer.'),
  });

  const abandon = useMutation({
    mutationFn: () => callsApi.cancelTransfer(transfer.originalCallId, transfer.transferId),
    onSuccess: () => {
      pendingTransferActions.clear(transfer.transferId);
      toast.success('Transfer cancelled.');
      // Back to exactly where you were: the original call, off hold. Through
      // `useOpenCall` so a desktop call window is FOCUSED rather than a second
      // one opened against a room this session is already in.
      openCall({
        callId: transfer.originalCallId,
        peerName: '',
        kind: 'audio',
        startsConnected: true,
      });
    },
    onError: () => toast.error('Could not cancel that transfer.'),
  });

  const busy = props.busy || finish.isPending || abandon.isPending;
  const onComplete = useCallback(() => finish.mutate(), [finish]);
  const onCancel = useCallback(() => abandon.mutate(), [abandon]);

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-warning/40 bg-warning-subtle px-4 py-2"
      role="region"
      aria-label="Transfer in progress"
    >
      <PhoneForwarded size={16} className="text-warning" aria-hidden />
      <p className="mr-auto text-sm text-foreground">
        Consulting {transfer.targetName}. Your other call is on hold.
      </p>
      <Button variant="ghost" disabled={busy} onClick={onCancel}>
        Cancel transfer
      </Button>
      <Button variant="secondary" disabled={busy} onClick={props.onMerge}>
        Merge calls
      </Button>
      <Button variant="primary" disabled={busy} onClick={onComplete}>
        Complete transfer
      </Button>
    </div>
  );
}
